import { classes, stylesheet } from 'typestyle';
import {
  Blue0,
  ButtonIcon,
  Gray100,
  Gray20,
  Red80,
  Tooltip,
  typeCompact12MediumProperties,
  typeCompact14MediumProperties,
  White,
} from '@traceair/tds';
import React, { ReactNode } from 'react';
import { InfoIcon } from '@traceair/webapp-icons';

const css = stylesheet({
  container: {
    position: 'relative',
    marginBottom: 6,
    boxShadow: '0 1px 2px rgba(16, 24, 41, 0.05)',
    borderRadius: 12,
    border: `1px solid ${Gray20}`,
    overflow: 'hidden',
  },
  containerInner: {
    position: 'relative',
    zIndex: 2,
    display: 'flex',
    padding: '11px 16px 11px 19px',
    gap: 10,
    alignItems: 'center',
  },
  containerWarning: {
    border: `1px solid ${Red80}`,
  },
  icon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    $nest: {
      svg: {
        flex: '0 0 auto',
      },
    },
  },
  title: {
    marginBottom: 4,
    ...typeCompact14MediumProperties,
  },
  note: {
    ...typeCompact12MediumProperties,
    color: Gray100,
  },
  tooltipIcon: {
    margin: '-6px 0 -5px 1px',
  },
  cancel: {
    marginLeft: 'auto',
  },
  footer: {
    position: 'relative',
    zIndex: 2,
  },
  progressBar: {
    position: 'absolute',
    left: 0,
    top: 0,
    height: '100%',
    background: Blue0,
  },
  progressBarCompleted: {
    background: White,
  },
});

interface Props {
  icon: ReactNode;
  title: string;
  note: string;
  removeIcon: ReactNode;
  onRemoveClick: () => void;
  progress?: number;
  isWarning?: boolean;
  disableCancel?: boolean;
  isDone: boolean;
  tooltip?: string;
  footer?: ReactNode;
}

export const UploadProgress = ({
  icon,
  title,
  note,
  removeIcon,
  onRemoveClick,
  progress,
  isWarning = false,
  disableCancel = false,
  isDone,
  tooltip,
  footer,
}: Props) => {
  const roundedProgress = progress ? Math.round(progress) : null;

  return (
    <div className={classes(css.container, isWarning && css.containerWarning)}>
      <div className={css.containerInner}>
        <div className={css.icon}>{icon}</div>

        <div>
          <div className={css.title}>{title}</div>
          <div className={css.note}>
            {note}
            {tooltip && (
              <Tooltip arrow description={tooltip} offset={4} placement="top" styleType="neutral-high" trigger="hover">
                <ButtonIcon
                  className={css.tooltipIcon}
                  style="tertiary"
                  icon={<InfoIcon color={Gray100} />}
                  size="xs"
                />
              </Tooltip>
            )}
          </div>
        </div>

        {!disableCancel && (
          <div className={css.cancel}>
            <ButtonIcon style="tertiary" size="s" type="black" icon={removeIcon} onClick={onRemoveClick} />
          </div>
        )}
      </div>

      {footer && <div className={css.footer}>{footer}</div>}

      {!!roundedProgress && (
        <div
          style={{ width: `${roundedProgress}%` }}
          className={classes(css.progressBar, isDone && css.progressBarCompleted)}
        />
      )}
    </div>
  );
};
